import React from 'react';
import { motion } from 'framer-motion';
import {
  Button,
  CollapsibleSection,
  EmptyState,
  Flex,
  ListCard,
  LoadingOrbit,
  Markdown,
  Spinner,
  Switch,
  TagBadge,
  Text,
} from '@chatsdet/ui';
import { FileText } from '@phosphor-icons/react';
import {
  usePageSkills,
  usePageSkill,
  useArchivePageSkill,
  useUnarchivePageSkill,
  useUpdatePageSkill,
} from '../usePageSkills';
import type { PageSkillListRow } from '../types';
import { EditPageSkillDialog } from './EditPageSkillDialog';
import { PageSkillVersionsDialog } from './PageSkillVersionsDialog';

type Props = {
  propertyId: string;
  /** The canonical page's URL — matched server-side with the runtime matcher. */
  pageUrl: string;
};

/**
 * The page skill(s) an agent would actually be handed on THIS page.
 *
 * Matching happens server-side via the same `matchesPageSkillPath` the runtime
 * uses, so this panel cannot drift from what the agent sees — a glob/regex
 * re-implementation in the client would be a second source of truth and would
 * eventually disagree.
 *
 * Progressive disclosure: each row is collapsed to its identity (title, pattern,
 * version, size) and the markdown body loads only when the row is expanded — the
 * list endpoint is deliberately skinny, so an unopened skill never pays for its
 * body.
 */
export function PageSkillPanel({ propertyId, pageUrl }: Props) {
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const { data, isLoading } = usePageSkills(propertyId, {
    pathname: pageUrl,
    includeArchived,
    enabled: !!pageUrl,
  });

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [historyFor, setHistoryFor] = React.useState<PageSkillListRow | null>(null);

  const { data: editing } = usePageSkill(propertyId, editingId);
  const update = useUpdatePageSkill(propertyId);
  const archive = useArchivePageSkill(propertyId);
  const unarchive = useUnarchivePageSkill(propertyId);

  const skills = data ?? [];
  const active = skills.filter((s) => !s.archivedAt);
  const totalBytes = active.reduce((sum, s) => sum + s.bodyBytes, 0);
  const accessory =
    active.length > 0 ? `${active.length} · ${totalBytes.toLocaleString()} B` : undefined;

  return (
    <>
      <ListCard
        title="Page skill"
        titleAccessory={accessory}
        description="What the browser agent reads before acting on this page."
        action={
          <Switch isSelected={includeArchived} onChange={setIncludeArchived} label="Archived" />
        }
      >
        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingOrbit />
          </div>
        ) : skills.length === 0 ? (
          <div className="p-4">
            <EmptyState
              size="sm"
              icon={<FileText size={20} />}
              title="No page skill here yet"
              description="The browser agent writes one the first time it works through this page — nothing to read yet."
            />
          </div>
        ) : (
          skills.map((skill, index) => (
            <PageSkillRow
              key={skill.id}
              propertyId={propertyId}
              skill={skill}
              index={index}
              onEdit={() => setEditingId(skill.id)}
              onHistory={() => setHistoryFor(skill)}
              onArchive={() => archive.mutate(skill.id)}
              onUnarchive={() => unarchive.mutate(skill.id)}
              archiveBusy={archive.isPending}
              unarchiveBusy={unarchive.isPending}
            />
          ))
        )}
      </ListCard>

      <EditPageSkillDialog
        isOpen={!!editingId}
        onClose={() => setEditingId(null)}
        skill={editing ?? null}
        isSaving={update.isPending}
        onSave={(edit) => update.mutateAsync({ skillId: editingId!, ...edit })}
      />

      <PageSkillVersionsDialog
        isOpen={!!historyFor}
        onClose={() => setHistoryFor(null)}
        propertyId={propertyId}
        skillId={historyFor?.id ?? null}
        pathPattern={historyFor?.pathPattern ?? ''}
      />
    </>
  );
}

type RowProps = {
  propertyId: string;
  skill: PageSkillListRow;
  index: number;
  onEdit: () => void;
  onHistory: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  archiveBusy: boolean;
  unarchiveBusy: boolean;
};

function PageSkillRow({
  propertyId,
  skill,
  index,
  onEdit,
  onHistory,
  onArchive,
  onUnarchive,
  archiveBusy,
  unarchiveBusy,
}: RowProps) {
  const [expanded, setExpanded] = React.useState(false);
  // Body loads only once the row is opened — keeps the collapsed list cheap.
  const { data: full, isLoading } = usePageSkill(propertyId, expanded ? skill.id : null);

  const header = (
    <div className="min-w-0 space-y-1">
      <Flex align="center" gap="sm" wrap>
        <Text weight="medium" className="truncate">
          {skill.title}
        </Text>
        {skill.archivedAt && <TagBadge color="gray" name="Archived" size="sm" />}
      </Flex>
      <Text size="xs" className="text-muted-foreground font-mono break-all">
        {skill.pathPattern} ({skill.matchType})
      </Text>
      <Flex gap="sm" wrap align="center">
        <Text size="xs" className="text-muted-foreground tabular-nums">
          v{skill.currentVersion} · {skill.bodyBytes.toLocaleString()} B · updated{' '}
          {new Date(skill.updatedAt).toLocaleDateString()}
        </Text>
        {skill.tags.map((tag) => (
          <TagBadge key={tag} color="blue" name={tag} size="sm" />
        ))}
      </Flex>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, delay: index * 0.03 }}
    >
      <div className="px-4 py-2">
        <CollapsibleSection
          header={header}
          hideBorder
          isExpanded={expanded}
          onExpandedChange={setExpanded}
        >
          <div className="space-y-3 pl-6">
            {isLoading || !full ? (
              <Flex align="center" gap="sm">
                <Spinner size="xs" />
                <Text size="sm" className="text-muted-foreground">
                  Loading skill…
                </Text>
              </Flex>
            ) : (
              <>
                {full.summary && (
                  <Text size="sm" className="text-muted-foreground text-pretty">
                    {full.summary}
                  </Text>
                )}
                <div className="overflow-x-auto rounded-lg border border-border bg-muted/30 p-4">
                  <Markdown className="prose prose-sm max-w-none">{full.body}</Markdown>
                </div>
                <Flex gap="sm" wrap>
                  <Button variant="outline" onPress={onEdit}>
                    Edit
                  </Button>
                  <Button variant="outline" onPress={onHistory}>
                    History
                  </Button>
                  {skill.archivedAt ? (
                    <Button variant="outline" isDisabled={unarchiveBusy} onPress={onUnarchive}>
                      Restore
                    </Button>
                  ) : (
                    <Button variant="outline" isDisabled={archiveBusy} onPress={onArchive}>
                      Archive
                    </Button>
                  )}
                </Flex>
              </>
            )}
          </div>
        </CollapsibleSection>
      </div>
    </motion.div>
  );
}
